import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const CONTRACTOR_SERVICES = [
  { value: 'builders', label: 'Builders', icon: 'fa-hard-hat' },
  { value: 'tilers', label: 'Tilers', icon: 'fa-th-large' },
  { value: 'roofers', label: 'Roofers', icon: 'fa-home' },
  { value: 'plumbers', label: 'Plumbers', icon: 'fa-faucet' },
  { value: 'electricians', label: 'Electricians', icon: 'fa-bolt' },
  { value: 'architects', label: 'Architects', icon: 'fa-drafting-compass' },
  { value: 'painters', label: 'Painters', icon: 'fa-paint-roller' },
  { value: 'landscapers', label: 'Landscapers', icon: 'fa-tree' },
  { value: 'carpenters', label: 'Carpenters', icon: 'fa-hammer' }
];

const ContractorRegistration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // Now starts at 1 (Business), 2 (Verification)
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [formData, setFormData] = useState({
    // Business Info
    businessName: '',
    contractorName: '',
    services: [],
    location: '',
    areasServed: '',
    contactNumber: '',
    whatsappNumber: '',
    businessEmail: '',
    description: '',
    // Verification Documents
    idDocument: null,
    cipcDocument: null,
    tradeCertificate: null
  });

  // Get logged-in user info on mount
  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    const userEmail = localStorage.getItem('user_email');
    const userName = localStorage.getItem('user_name');
    
    if (!userId) {
      toast.error('Please log in first to register as a contractor');
      navigate('/auth');
      return;
    }
    
    setCurrentUser({
      id: userId,
      email: userEmail,
      name: userName
    });
    
    // Pre-fill contractor name with user's name
    setFormData(prev => ({
      ...prev,
      contractorName: userName || '',
      businessEmail: userEmail || ''
    }));
  }, [navigate]);

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleFileChange = (field, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep1 = () => {
    if (!formData.businessName || !formData.contractorName || formData.services.length === 0) {
      toast.error('Please fill in business name, contractor name, and select at least one service');
      return false;
    }
    if (!formData.location || !formData.contactNumber || !formData.whatsappNumber) {
      toast.error('Please provide location and contact details');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!formData.idDocument) {
      toast.error('ID document is required for verification');
      return;
    }

    if (!currentUser?.id) {
      toast.error('User session not found. Please log in again.');
      navigate('/auth');
      return;
    }

    try {
      setLoading(true);

      // Create contractor profile linked to existing user
      const areasArray = formData.areasServed
        .split(',')
        .map(a => a.trim())
        .filter(a => a.length > 0);

      await axios.post(`${API}/contractors`, {
        userId: currentUser.id,
        businessName: formData.businessName,
        contractorName: formData.contractorName,
        services: formData.services,
        location: formData.location,
        areasServed: areasArray,
        contactNumber: formData.contactNumber,
        whatsappNumber: formData.whatsappNumber,
        email: formData.businessEmail || currentUser.email,
        description: formData.description,
        idDocumentUrl: formData.idDocument || '',
        cipcDocumentUrl: formData.cipcDocument || '',
        tradeCertificateUrl: formData.tradeCertificate || ''
      });

      // Update user mode to Contractor
      localStorage.setItem('app_user_mode', 'Contractor');

      toast.success('Registration successful! Your profile is pending verification.');
      navigate('/contractor/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      const errorMsg = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-emerald-600"></i>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            <i className="fas fa-hard-hat text-emerald-600 mr-3"></i>
            Contractor Registration
          </h1>
          <p className="text-gray-600 mt-2">Complete your business profile to start receiving clients</p>
          
          {/* Show logged-in user info */}
          <div className="mt-4 inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm">
            <i className="fas fa-user-check"></i>
            <span>Logged in as <strong>{currentUser.name || currentUser.email}</strong></span>
          </div>
        </div>

        {/* Progress Steps - Now 2 steps only */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center">
            {[1, 2].map((s, i) => (
              <React.Fragment key={s}>
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step >= s ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s}
                </div>
                {i < 1 && (
                  <div className={`w-24 h-1 ${step > s ? 'bg-emerald-600' : 'bg-gray-200'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="flex justify-center mb-8 text-sm text-gray-600">
          <span className={`mx-6 ${step === 1 ? 'font-semibold text-emerald-600' : ''}`}>Business Details</span>
          <span className={`mx-6 ${step === 2 ? 'font-semibold text-emerald-600' : ''}`}>Verification</span>
        </div>

        {/* Step 1: Business Info (Previously Step 2) */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
              <CardDescription>Tell us about your business</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="businessName">Business Name *</Label>
                <Input
                  id="businessName"
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  placeholder="ABC Construction"
                  data-testid="business-name-input"
                />
              </div>
              <div>
                <Label htmlFor="contractorName">Contractor Name *</Label>
                <Input
                  id="contractorName"
                  value={formData.contractorName}
                  onChange={(e) => setFormData({ ...formData, contractorName: e.target.value })}
                  placeholder="John Builder"
                  data-testid="contractor-name-input"
                />
              </div>

              <div>
                <Label>Services Offered *</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {CONTRACTOR_SERVICES.map((service) => (
                    <div
                      key={service.value}
                      onClick={() => handleServiceToggle(service.value)}
                      className={`p-3 rounded-lg border-2 cursor-pointer text-center transition-all ${
                        formData.services.includes(service.value)
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`service-option-${service.value}`}
                    >
                      <i className={`fas ${service.icon} text-lg ${
                        formData.services.includes(service.value) ? 'text-emerald-600' : 'text-gray-400'
                      }`}></i>
                      <p className="text-xs mt-1">{service.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="location">Primary Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Johannesburg"
                  data-testid="location-input"
                />
              </div>
              <div>
                <Label htmlFor="areasServed">Areas Served (comma separated)</Label>
                <Input
                  id="areasServed"
                  value={formData.areasServed}
                  onChange={(e) => setFormData({ ...formData, areasServed: e.target.value })}
                  placeholder="Sandton, Randburg, Midrand"
                  data-testid="areas-served-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contactNumber">Contact Number *</Label>
                  <Input
                    id="contactNumber"
                    value={formData.contactNumber}
                    onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
                    placeholder="+27 12 345 6789"
                    data-testid="contact-number-input"
                  />
                </div>
                <div>
                  <Label htmlFor="whatsappNumber">WhatsApp Number *</Label>
                  <Input
                    id="whatsappNumber"
                    value={formData.whatsappNumber}
                    onChange={(e) => setFormData({ ...formData, whatsappNumber: e.target.value })}
                    placeholder="+27 12 345 6789"
                    data-testid="whatsapp-number-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="businessEmail">Business Email (optional)</Label>
                <Input
                  id="businessEmail"
                  type="email"
                  value={formData.businessEmail}
                  onChange={(e) => setFormData({ ...formData, businessEmail: e.target.value })}
                  placeholder="info@abcconstruction.co.za"
                  data-testid="business-email-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Business Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tell potential clients about your experience and services..."
                  rows={4}
                  data-testid="description-input"
                />
              </div>

              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => validateStep1() && setStep(2)}
                data-testid="next-step-1"
              >
                Continue to Verification <i className="fas fa-arrow-right ml-2"></i>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Verification Documents (Previously Step 3) */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Verification Documents</CardTitle>
              <CardDescription>
                Upload documents to verify your identity and business. This helps build trust with clients.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ID Document */}
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                <Label className="font-semibold">SA ID Document * <span className="text-red-500">(Required)</span></Label>
                <p className="text-sm text-gray-500 mb-2">Upload a copy of your South African ID</p>
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange('idDocument', e.target.files[0])}
                  data-testid="id-document-input"
                />
                {formData.idDocument && (
                  <p className="text-sm text-emerald-600 mt-2">
                    <i className="fas fa-check-circle mr-1"></i> File selected
                  </p>
                )}
              </div>

              {/* CIPC Document */}
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                <Label className="font-semibold">Company Registration (CIPC) <span className="text-gray-400">(Optional)</span></Label>
                <p className="text-sm text-gray-500 mb-2">Upload your company registration documents</p>
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange('cipcDocument', e.target.files[0])}
                  data-testid="cipc-document-input"
                />
                {formData.cipcDocument && (
                  <p className="text-sm text-emerald-600 mt-2">
                    <i className="fas fa-check-circle mr-1"></i> File selected
                  </p>
                )}
              </div>

              {/* Trade Certificate */}
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                <Label className="font-semibold">Trade Certificate <span className="text-gray-400">(Optional)</span></Label>
                <p className="text-sm text-gray-500 mb-2">Upload your trade certification</p>
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange('tradeCertificate', e.target.files[0])}
                  data-testid="trade-certificate-input"
                />
                {formData.tradeCertificate && (
                  <p className="text-sm text-emerald-600 mt-2">
                    <i className="fas fa-check-circle mr-1"></i> File selected
                  </p>
                )}
              </div>

              {/* Terms */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Subscription Information</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li><i className="fas fa-check text-emerald-600 mr-2"></i>1-month FREE trial included</li>
                  <li><i className="fas fa-check text-emerald-600 mr-2"></i>R150/month after trial</li>
                  <li><i className="fas fa-check text-emerald-600 mr-2"></i>Portfolio visibility to thousands of homeowners</li>
                  <li><i className="fas fa-check text-emerald-600 mr-2"></i>Cancel anytime</li>
                </ul>
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="back-step-2">
                  <i className="fas fa-arrow-left mr-2"></i> Back
                </Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleSubmit}
                  disabled={loading}
                  data-testid="submit-registration"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i> Registering...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check mr-2"></i> Complete Registration
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Back to dashboard link */}
        <div className="text-center mt-6">
          <p className="text-gray-600">
            Changed your mind?{' '}
            <button onClick={() => navigate('/dashboard')} className="text-emerald-600 hover:underline">
              Return to Dashboard
            </button>
          </p>
        </div>
      </div>
    </main>
  );
};

export default ContractorRegistration;
